// SPV Pool v1.0 — typed transport for /api/spv-pools/*. Mutation hooks
// live in @/hooks/capitalMarkets/useSpvPools; this module is the raw
// transport layer following the @/api/deals convention.

import { api } from "./client";
import type { PagedResult } from "@/types/api";
import type {
  AccountingStatusDto,
  CashFlowPeriod,
  ClosingDetailResponse,
  ClosingExecuteRequest,
  ClosingExecuteResponse,
  ClosingPreview,
  ClosingPreviewRequest,
  ClosingSummary,
  CompositionValidationResult,
  ContributeResult,
  EligibleLoanDto,
  FinalizePoolRequest,
  FinalizePoolResponse,
  IDUPDocumentDto,
  IDUPPreviewDto,
  IDUPStatus,
  PoolCashFlowDto,
  PoolCompositionDto,
  PoolCompositionStatus,
  PoolEligibilityCriteriaDto,
  PoolEligibilityCriteriaUpdate,
  PoolFormationEventDto,
  PoolMath,
  PoolSegmentTriggerEventDto,
  ReserveFundLedgerEntry,
  ReserveManualAdjustmentRequest,
  SpvPoolCreate,
  SpvPoolDto,
  SpvPoolListQuery,
  SpvPoolUpdate,
  SsnDto,
  StageGateCheckResult,
  StageGateGroup,
  VfiPreview,
  VfiPreviewRequest,
  VfiResult,
} from "@/types/spvPool";

export const spvPoolsApi = {
  list: (query?: SpvPoolListQuery) =>
    api.get<PagedResult<SpvPoolDto>>("/spv-pools", query),
  get: (id: string) => api.get<SpvPoolDto>(`/spv-pools/${id}`),
  create: (body: SpvPoolCreate) => api.post<SpvPoolDto>("/spv-pools", body),
  update: (id: string, body: SpvPoolUpdate) =>
    api.put<SpvPoolDto>(`/spv-pools/${id}`, body),
  delete: (id: string) => api.delete<void>(`/spv-pools/${id}`),

  math: (id: string) => api.get<PoolMath>(`/spv-pools/${id}/math`),
  advanceStatus: (id: string, target: string) =>
    api.post<SpvPoolDto>(`/spv-pools/${id}/advance-status`, { target }),

  // Payment System v3.0 PR 11 — OC trigger toggle.
  setOcTrigger: (id: string, active: boolean) =>
    api.post<{ poolId: string; active: boolean; ocRatio: number }>(
      `/spv-pools/${id}/oc-trigger`,
      { active },
    ),

  // Stage-gate certifications (PR 8) ---------------------------------------
  stageGateCheck: (id: string, targetStatus: string) =>
    api.get<StageGateCheckResult>(`/spv-pools/${id}/stage-gate-check`, {
      targetStatus,
    }),
  stageGates: (id: string) =>
    api.get<StageGateGroup[]>(`/spv-pools/${id}/stage-gates`),

  // Composition -----------------------------------------------------------
  composition: (id: string, status?: PoolCompositionStatus) =>
    api.get<PoolCompositionDto[]>(
      `/spv-pools/${id}/composition`,
      status ? { status } : undefined,
    ),
  eligibleSegments: (id: string) =>
    api.get<EligibleLoanDto[]>(`/spv-pools/${id}/composition/eligible-segments`),
  propose: (id: string, segmentIds: string[]) =>
    api.post<PoolCompositionDto[]>(`/spv-pools/${id}/composition/propose`, {
      segmentIds,
    }),
  validate: (id: string) =>
    api.post<CompositionValidationResult>(
      `/spv-pools/${id}/composition/validate`,
    ),
  commit: (id: string, segmentIds: string[]) =>
    api.post<CompositionValidationResult>(
      `/spv-pools/${id}/composition/commit`,
      { segmentIds },
    ),
  contribute: (id: string, segmentIds: string[], confirmPoolName: string) =>
    api.post<ContributeResult>(`/spv-pools/${id}/composition/contribute`, {
      segmentIds,
      confirmPoolName,
    }),

  // Eligibility criteria --------------------------------------------------
  eligibilityCriteria: (id: string) =>
    api.get<PoolEligibilityCriteriaDto | null>(
      `/spv-pools/${id}/eligibility-criteria`,
    ),
  updateEligibilityCriteria: (
    id: string,
    body: PoolEligibilityCriteriaUpdate,
  ) =>
    api.put<PoolEligibilityCriteriaDto>(
      `/spv-pools/${id}/eligibility-criteria`,
      body,
    ),

  // IDUP ------------------------------------------------------------------
  idup: (id: string) => api.get<IDUPDocumentDto[]>(`/spv-pools/${id}/idup`),
  regenerateIdup: (id: string) =>
    api.post<IDUPDocumentDto>(`/spv-pools/${id}/idup/regenerate`),
  updateIdupStatus: (id: string, idupId: string, status: IDUPStatus) =>
    api.put<IDUPDocumentDto>(`/spv-pools/${id}/idup/${idupId}/status`, {
      status,
    }),
  idupPreview: (id: string, idupId: string) =>
    api.get<IDUPPreviewDto>(`/spv-pools/${id}/idup/preview/${idupId}`),

  // Cash Flow (PR 6) ------------------------------------------------------
  cashFlow: {
    getCashFlow: (
      id: string,
      period: CashFlowPeriod,
      start: string,
      end: string,
    ) =>
      api.get<PoolCashFlowDto>(`/spv-pools/${id}/cash-flow`, {
        period,
        start,
        end,
      }),
    getAccountingStatus: (id: string) =>
      api.get<AccountingStatusDto>(`/spv-pools/${id}/cash-flow/accounting-status`),
    postReserveManualAdjustment: (
      id: string,
      body: ReserveManualAdjustmentRequest,
    ) =>
      api.post<ReserveFundLedgerEntry>(
        `/spv-pools/${id}/reserve-fund/manual-adjustment`,
        body,
      ),
  },

  // Payment System v3.0 PR 6 — pool formation ceremony (design spec §8.1).
  finalize: (id: string, body: FinalizePoolRequest) =>
    api.post<FinalizePoolResponse>(`/spv-pools/${id}/finalize`, body),
  formation: (id: string) =>
    api.get<PoolFormationEventDto | null>(`/spv-pools/${id}/formation`),
  triggers: (id: string) =>
    api.get<PoolSegmentTriggerEventDto[]>(`/spv-pools/${id}/triggers`),
  ssns: (id: string) =>
    api.get<SsnDto[]>(`/spv-pools/${id}/ssns`),

  // Payment System v3.0 PR 7 — multi-investor closings (design spec §8.2).
  closings: {
    list: (poolId: string) =>
      api.get<ClosingSummary[]>(`/spv-pools/${poolId}/closings`),
    preview: (poolId: string, body: ClosingPreviewRequest) =>
      api.post<ClosingPreview>(`/spv-pools/${poolId}/closings/preview`, body),
    execute: (poolId: string, body: ClosingExecuteRequest) =>
      api.post<ClosingExecuteResponse>(`/spv-pools/${poolId}/closings`, body),
  },

  // Payment System v3.0 PR 7 — VFI inflow recording (design spec §8.3).
  vfi: {
    preview: (poolId: string, body: VfiPreviewRequest) =>
      api.post<VfiPreview>(`/spv-pools/${poolId}/vfi/preview`, body),
    record: (poolId: string, body: VfiPreviewRequest) =>
      api.post<VfiResult>(`/spv-pools/${poolId}/vfi`, body),
  },
};

// Payment System v3.0 PR 7 — closing detail lives on /api/closings/{id}
// (not nested under the pool route) per the spec.
export const closingsApi = {
  get: (paymentId: string) =>
    api.get<ClosingDetailResponse>(`/closings/${paymentId}`),
};
